/**
 * @fileoverview Archive Client mutation hook.
 * @description Archives a client (POST /sales/clients/{tenantId}/deactivate) through
 * `useIdempotentMutation`, whose kept `Idempotency-Key` makes a retry after an unknown outcome a
 * replay. The command takes no `If-Match`: the backend's state refusal decides.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useIdempotentMutation } from '@/features/sales/hooks/useIdempotentMutation'
import { deactivateClient } from '@/features/sales/services/sales-clients.service'

import type { ClientDeactivated } from '@/features/sales/types/sales-client.types'

/**
 * @description Archives a client, then refreshes every sales query so the grid and tiles drop it.
 * @param {(result: ClientDeactivated) => void} [onSuccess] - Called after a success.
 * @returns {ReturnType<typeof useIdempotentMutation>} The mutation (mutate with the client's id).
 */
export function useArchiveClient(onSuccess?: (result: ClientDeactivated) => void) {
  return useIdempotentMutation<string, ClientDeactivated>({
    action: 'archive-client',
    /**
     * @description Sends the archive under the key it is given.
     * @param {string} tenantId - The client.
     * @param {string} key - The Idempotency-Key.
     * @returns {Promise<ClientDeactivated>} The client's status.
     */
    send: (tenantId, key) => deactivateClient(tenantId, key),
    onSuccess,
  })
}
